/**
 * Client Environment Singleton
 *
 * Provides the ClientEnv singleton for browser contexts.
 * Supports two initialization patterns:
 * - React tree: ClientEnvProvider calls initializeClientEnv()
 * - Early access: getClientEnv() lazily initializes from __CLIENT_CONFIG__
 *
 * @example
 * ```typescript
 * // In React components (preferred):
 * const { config, logger, services } = useClientEnv()
 *
 * // Outside React tree (e.g., global error boundary):
 * import { getClientEnv } from '@/env/client'
 * const { logger, services } = getClientEnv()
 * ```
 *
 * @see src/app/hooks/use-client-env.tsx for the React provider and hook
 * @see docs/config-management.md for detailed documentation
 */

import type { ClientConfig } from '@/config/schema'
import { initializeClientLogger, initializeDatadog } from '@/observability/client'
import type { Logger } from '@/observability/logger/types'
import type {
    ClientLoggerInitConfig,
    DatadogClient,
    DatadogInitConfig,
} from '@/observability/types'

import { getGlobal, KEYS, setGlobal } from './globals'

// =============================================================================
// TYPES
// =============================================================================

export interface ClientEnv {
    config: ClientConfig
    logger: Logger
    services: {
        datadog: DatadogClient
    }
}

// =============================================================================
// GLOBALS
// =============================================================================

declare global {
    var __CLIENT_ENV__: ClientEnv | undefined
    var __CLIENT_CONFIG__: ClientConfig | undefined
}

// =============================================================================
// FACTORIES
// =============================================================================

/**
 * Factory functions for creating service instances.
 * Each factory receives only the config subset it needs.
 */
export interface ClientEnvServiceFactories {
    logger?: (config: ClientLoggerInitConfig) => Logger
    datadog?: (config: DatadogInitConfig) => DatadogClient
}

/**
 * Default factories that create the standard service implementations.
 *
 * Note: Datadog SDK adds ~60KB gzipped to the client bundle. If Core Web Vitals
 * become a concern, consider lazy-loading via requestIdleCallback.
 */
export const defaultClientEnvFactories: Required<ClientEnvServiceFactories> = {
    logger: (config) => initializeClientLogger(config),
    datadog: (config) => initializeDatadog(config),
}

// =============================================================================
// CORE FUNCTIONS
// =============================================================================

/**
 * Creates a ClientEnv instance from config.
 * This is a pure function - no async operations, no side effects.
 *
 * Exported for testing to create isolated instances without touching the singleton.
 *
 * @example
 * ```typescript
 * const mockConfig = { ... }
 * const env = createClientEnv(mockConfig, {
 *     datadog: () => mockDatadog,
 * })
 * ```
 */
export function createClientEnv(
    config: ClientConfig,
    factories: ClientEnvServiceFactories = {},
): ClientEnv {
    const resolvedFactories = { ...defaultClientEnvFactories, ...factories }

    const loggerConfig: ClientLoggerInitConfig = {
        logs: config.logs,
        datadog: { logs: config.datadog.logs },
    }

    const datadogConfig: DatadogInitConfig = {
        env: config.env,
        service: config.service,
        datadog: config.datadog,
    }

    return {
        config,
        logger: resolvedFactories.logger(loggerConfig),
        services: {
            datadog: resolvedFactories.datadog(datadogConfig),
        },
    }
}

/**
 * Initializes the ClientEnv singleton from provided config.
 *
 * Called by ClientEnvProvider when it first renders. Subsequent calls
 * with the same config return the existing instance.
 *
 * Uses globalThis to persist across HMR and Turbopack module reloads in dev mode.
 *
 * @param config - The client configuration
 * @param factories - Optional custom service factories
 * @returns The initialized ClientEnv
 */
export function initializeClientEnv(
    config: ClientConfig,
    factories?: ClientEnvServiceFactories,
): ClientEnv {
    const existing = getGlobal<ClientEnv>(KEYS.CLIENT_ENV)
    if (existing) {
        return existing
    }

    const env = createClientEnv(config, factories)
    setGlobal(KEYS.CLIENT_ENV, env)
    env.logger.withMetadata({ env: config.env }).debug('ClientEnv initialized')
    return env
}

/**
 * Returns the ClientEnv singleton.
 *
 * This function supports lazy initialization: if ClientEnv hasn't been initialized
 * but __CLIENT_CONFIG__ is available (set by an inline script in the layout),
 * it will initialize ClientEnv automatically. This ensures ClientEnv is available
 * even in code that runs before the React tree mounts (e.g., global error boundary).
 *
 * @throws Error if neither ClientEnv nor ClientConfig is available
 */
export function getClientEnv(): ClientEnv {
    const env = getGlobal<ClientEnv>(KEYS.CLIENT_ENV)
    if (env) {
        return env
    }

    // Lazy initialization from config set by inline script in layout
    const config = getGlobal<ClientConfig>(KEYS.CLIENT_CONFIG)
    if (config) {
        return initializeClientEnv(config)
    }

    throw new Error(
        '[ClientEnv] Not initialized. ' +
            'For React components: ensure ClientEnvProvider has rendered. ' +
            'For early access: ensure the locale layout rendered ClientConfigScript. ' +
            'See src/app/hooks/use-client-env.tsx for details.',
    )
}
